import ThreeViewObj, { CameraSettings } from "@/Three/ThreeView"
import DragAndDrop from "@/Three/DragAndDrop"
import DragAndDropPlaneDirectionStrategy from "@/Three/DragAndDropPlaneDirectionStrategy"
import { TemsWebSetting } from "@/Settings/SettingsManager"
import { TemsObject3D } from "@/CommonTypes"
import SiteManagerView from "@/Site/SiteManagerView"
import TemsWebEvents from "@/TemsWebEvents"
import { MeshObject, ElementViewModel, MeshInfo, MeshGroup, MeshTransform, MeshRoot } from "@/TemsManagerWebInterface"
import THREE from "three"
import ThreeScene from "@/Three/ThreeScene";

export default class SiteView extends ThreeViewObj {

    private static CameraSettings = new TemsWebSetting<CameraSettings>("SiteViewCameraSettings", new CameraSettings(
        new THREE.Vector3(-15, 7.5, 20),
        new THREE.Vector3(0, 0, 0)
    ));


    // Edit Related properties and fields
    private isInEditMode: boolean;
    
    private coordinateSystemHud: CoordinateSystemHud;

    private siteManagerView: SiteManagerView;

    constructor(siteManagerView: SiteManagerView) {
        super(SiteView.CameraSettings, (view: ThreeViewObj, scene: ThreeScene) => {
            return [ this.generateDragAndDropPlugin(view, scene) ];
        });
        this.siteManagerView = siteManagerView;
        TemsWebEvents.subscribeEvent(TemsWebEvents.ElementSelected, (e, elementId) => {
            this.getScene().recreateMeshById(elementId);
        });
    }

    private generateDragAndDropPlugin(view: ThreeViewObj, scene: ThreeScene) {
        var dragNDropPlugin = new DragAndDrop(view, scene, new DragAndDropPlaneDirectionStrategy(view.getScene(), this.getControls()));
        dragNDropPlugin.onSelect((mesh) => {
            if (mesh && mesh.meshInfo) {
                this.siteManagerView.selectElement(mesh.meshInfo.Id);
            } else {
                this.siteManagerView.selectElement(null);
            }
        });
        dragNDropPlugin.setMovableChecker((selection) => {
            return this.isInEditMode
                && this.siteManagerView.isElementSelected(selection.meshInfo.Id);
        });
        dragNDropPlugin.onMove((mesh: TemsObject3D, oldPos: THREE.Vector3, newPos: THREE.Vector3) => {
            mesh.position.copy(newPos);
            if ("meshInfo" in mesh) {
                const miTransform = mesh.meshInfo.Transform;
                if (miTransform) {
                    miTransform.PositionX = newPos.x;
                    miTransform.PositionY = newPos.y;
                    miTransform.PositionZ = newPos.z;
                }
            }
        }); 
        return dragNDropPlugin;
    }

    public get IsInEditMode(): boolean {
        return this.isInEditMode;
    }

    public set IsInEditMode(isInEditMode: boolean) {
        this.isInEditMode = isInEditMode;
    }

    public init(elementScenery: JQuery, rendererHeight: number): void {
        TemsWebEvents.subscribeEvent(TemsWebEvents.MeshUpdated, (evt, mesh: TemsObject3D) => {
            this.updateTriggerIfNecessary(mesh);
        });

        super.init(elementScenery, rendererHeight); 

        this.coordinateSystemHud = new CoordinateSystemHud(
            elementScenery.children(".coordinateSystemHud"),
            this.camera,
            this.getControls()
        );
        this.handleOnResize(); // resize again, to get the initial size correct
    }

    public get latestResultMeshes(): TemsObject3D[] {
        return (this.hasScene())
            ? this.getScene().getMeshes(
                mesh => (mesh.meshInfo
                    && mesh.meshInfo.Id
                    && mesh.meshInfo.Id.indexOf("Latest_") === 0))
            : [];
    }
    
    /*
     * Sets a new site and creates all required elements in the DOM / WebGL
     */
    public setScene(scene: ThreeScene) {
        super.setScene(scene);
        if (scene == null) {
            this.coordinateSystemHud.domElement.toggle(false);
        } else {
            this.coordinateSystemHud.domElement.toggle(true);
            TemsWebEvents.publishEvent(TemsWebEvents.SceneLoaded);
        }
        this.renderFrame();
    }

    handleOnResize() {
        super.handleOnResize();
        if (this.coordinateSystemHud) {
            const rendererWidth = this.$elementScenery.innerWidth() * 0.25;
            const rendererHeight = this.$elementScenery.innerHeight() * 0.25;

            const size = Math.min(175, Math.max(rendererHeight, rendererWidth));

            this.coordinateSystemHud.setSize(size, size);
        }
    }

    protected onBeforeRender() {
        super.onBeforeRender();
        if (this.coordinateSystemHud) {
            this.coordinateSystemHud.updateCamera();
        }
    }

    public requestSiteScene() {
        return $.getJSON("/api/Info/GetSiteMeshInfos", null, (siteMeshInfos: any[]) => { 
            var scene = (siteMeshInfos && siteMeshInfos.length > 0)
                ? new ThreeScene(siteMeshInfos, (scene) => {
                    var light1 = new THREE.SpotLight(0xffffff, 1.0);
                    light1.position.set(0, 10, 60);
                    scene.add(light1);
                    var light2 = new THREE.SpotLight(0xffffff, 1.0);
                    light2.position.set(0, 10, -60);
                    scene.add(light2);
                }) : null;
            this.setScene(scene);
            if (this.IsInEditMode && scene) {
                this.getScene().getMeshes(() => true).map((m) => {
                    TemsWebEvents.publishEvent(TemsWebEvents.MeshUpdated, [m]);
                });
            }
        });
    }

    /*
     * Update DOM / WebGL when Health or States are changed
     */
    public displayElementState(element: ElementViewModel) {
        var recursiveDisplayElementState = (meshInfo: MeshInfo) => {
            if (meshInfo.hasOwnProperty("RequiredStates")) {
                meshInfo.IsVisible = this.determineVisibility(element.States, (<MeshObject> meshInfo).RequiredStates);
            }


            if (meshInfo.hasOwnProperty("mesh")) {
                (meshInfo as any).mesh.visible = meshInfo.IsVisible;
            }

            if (meshInfo.hasOwnProperty("Children")) {
                let mg = meshInfo as MeshGroup;
                for (var subMeshInfo of mg.Children) {
                    // actual devices (compared to their own parts) have the tell that we changed their ID to not contain dashes, the purpose is to not descend into sub devices. because the element.State for them is different.
                    if (subMeshInfo.Id.indexOf("-") >= 0) {
                        recursiveDisplayElementState(subMeshInfo);
                    }
                }
            }
        };

        let object3D; 

        if (this.hasScene()) {
            if (object3D = this.getScene().getObject3DFromMeshId(element.Id)) {
                recursiveDisplayElementState(object3D.meshInfo);
            }
        } else {
            console.warn("Cannot DisplayState because scene is missing");
        }
        
        // force a render call, because a visibility might have changed
        this.renderFrame();
    }

    /*
     * Calculates whether one of the required state-lists is covered by the given states-list
     */
    private determineVisibility(states: string[], requiredStateList: string[][]) {
        if (states && requiredStateList && requiredStateList.length) {
            // Helper function
            const stringSetIsInSuperSet = (set: string[], superSet: string[]): boolean => {
                for (let entry of set) {
                    if ($.inArray(entry, superSet) === -1) {
                        return false;
                    }
                }

                return true;
            };

            // Loop over all possible state combinations, if a combination matches the states -> return true
            for (let requiredStates of requiredStateList) {
                if (stringSetIsInSuperSet(requiredStates, states)) {
                    return true;
                }
            }

            return false;
        }

        return true;
    }

    getDevicePosition(id: string): MeshTransform {
        const allMeshes = this.getScene().getMeshes(m => (m.meshInfo && m.meshInfo.Id === id));
        if (allMeshes.length > 0) {
            return allMeshes[0].meshInfo.Transform;
        }

        return undefined;
    }

    updateMeshTransform(id: string, newTransform: MeshTransform) {
        this.updateMeshGeneric(id, m => {
            m.meshInfo.Transform = newTransform;

            m.position.x = m.meshInfo.Transform.PositionX;
            m.position.y = m.meshInfo.Transform.PositionY;
            m.position.z = m.meshInfo.Transform.PositionZ;

            m.rotation.x = m.meshInfo.Transform.AngleX;
            m.rotation.y = m.meshInfo.Transform.AngleY;
            m.rotation.z = m.meshInfo.Transform.AngleZ;

            m.scale.x = m.meshInfo.Transform.ScaleX;
            m.scale.y = m.meshInfo.Transform.ScaleY;
            m.scale.z = m.meshInfo.Transform.ScaleZ;
        });
    }

    updateMeshPositionX(id: string, newValue: number): void {
        this.updateMeshGeneric(id, m => { m.position.x = newValue; m.meshInfo.Transform.PositionX = newValue; });
    }
    updateMeshPositionY(id: string, newValue: number): void {
        this.updateMeshGeneric(id, m => { m.position.y = newValue; m.meshInfo.Transform.PositionY = newValue; });
    }
    updateMeshPositionZ(id: string, newValue: number): void {
        this.updateMeshGeneric(id, m => { m.position.z = newValue; m.meshInfo.Transform.PositionZ = newValue; });
    }

    /* NOTE
     mesh.position and meshInfo.Transform, aren't linked; now position and rotation are computed in three.js by decomposing the transformation matrix
     This also means we need to apply all angles every time one changes 
     Ex: [x=0,y=-180,z=0] => matrix => orientation => [x=-180, y=0, z=-180]
         -->  change y from -180 to -179 would result in a displayed: [x=-180, y=-179, z=-180]
     */
    updateMeshRotationX(id: string, newValue: number): void {
        const rad = THREE.Math.degToRad(newValue);
        this.updateMeshGeneric(id, m => {
            m.rotation.x = rad;
            m.rotation.y = m.meshInfo.Transform.AngleY;
            m.rotation.z = m.meshInfo.Transform.AngleZ;
            m.meshInfo.Transform.AngleX = rad;
        });
    }
    updateMeshRotationY(id: string, newValue: number): void {
        const rad = THREE.Math.degToRad(newValue);
        this.updateMeshGeneric(id, m => {
            m.rotation.x = m.meshInfo.Transform.AngleX;
            m.rotation.y = rad;
            m.rotation.z = m.meshInfo.Transform.AngleZ;
            m.meshInfo.Transform.AngleY = rad;
        });
    }
    updateMeshRotationZ(id: string, newValue: number): void {
        const rad = THREE.Math.degToRad(newValue);
        this.updateMeshGeneric(id, m => {
            m.rotation.x = m.meshInfo.Transform.AngleX;
            m.rotation.y = m.meshInfo.Transform.AngleY;
            m.rotation.z = rad;
            m.meshInfo.Transform.AngleZ = rad;
        });
    }

    updateMeshGeneric(id: string, updateFunction: (mesh: any) => void /* TODO mesh should be typed stronger, make a TemsMeshObject3D (to contain meshInfo) */) {
        if (this.hasScene()) {
            var allMeshes = this.getScene().getMeshes(m => (m.meshInfo && m.meshInfo.Id === id));
            for (var i = 0, meshesLength = allMeshes.length; i < meshesLength; i++) {
                updateFunction(allMeshes[i]);
                TemsWebEvents.publishEvent(TemsWebEvents.MeshUpdated, [allMeshes[i]]);
            }

            this.renderFrame();
        }
    }
    
    private updateTriggerIfNecessary(mesh: TemsObject3D) {
        // If this object has triggers attached to it, we need to update those meshes as well
        const triggerGroup = this.getScene().getObject3DFromMeshId(mesh.meshInfo.Id + "_triggers");
        if (triggerGroup) {
            triggerGroup.position.x = mesh.position.x;
        }
    }  
    public updateLatestResults(latestResultIds: string[]) {
        if (latestResultIds && this.hasScene()) {
            // find existing meshes and their Ids
            let latestMeshIds = this.latestResultMeshes.map(mesh => mesh.meshInfo.Id);

            // Find if we need to add a new vehicle or if it is still present
            for (let latestResultId of latestResultIds) {
                var meshId = "Latest_" + latestResultId;
                var arrayIndex = $.inArray(meshId, latestMeshIds);
                if (arrayIndex > -1) {
                    latestMeshIds.splice(arrayIndex, 1); // remaining latestMeshIds will get cleaned, thus remove from the list because it still exists
                } else {
                    // This is a new latest element
                    var dummyMeshObject = this.getScene().addDummyElement(meshId, 0, false);
                    dummyMeshObject.updateMatrix();   // Force a calulation to the matrix
                    dummyMeshObject.matrixAutoUpdate = false; // disable further updates; Intention is to not let Three.js do the update. There seems to be a problem if the tab is not focused

                    this.reloadResult(meshId, latestResultId);
                }
            }

            // remove all meshes that aren't in the server list anymore (they would have been removed from the Ids-list otherwise in the section above)
            for (let i = 0; i < latestMeshIds.length; i++) {
                this.getScene().removeMeshById(latestMeshIds[i]);
            }
        }
    }

    private reloadResult(meshId: string, resultId: string) {
        $.getJSON("/api/ResultExtension/GetMesh/" + resultId,
            (resultMesh: MeshRoot) => {
                if (resultMesh) {
                    resultMesh.Id = meshId; // Replace with the actual dummy Id
                    resultMesh.Transform = resultMesh.LocationTransform;
                    this.getScene().updateMesh(resultMesh, true);
                }
            }
        );
    }
}

class CoordinateSystemHud {
    public domElement: JQuery;
    private cameraControl: THREE.OrbitControls;
    private camera: THREE.Camera;
    private coordinateSystemVis: CoordinateSystemArrows;
    private coordinateSystemCamera: THREE.PerspectiveCamera;
    private coordinateSystemScene: THREE.Scene;
    private coordinateSystemRender: THREE.WebGLRenderer;

    constructor(coordinateSystemHudDom: JQuery, camera: THREE.Camera, cameraControl: THREE.OrbitControls) {
        this.camera = camera;
        this.cameraControl = cameraControl;
        this.domElement = coordinateSystemHudDom;

        this.coordinateSystemRender = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true
        });

        this.coordinateSystemVis = new CoordinateSystemArrows();

        this.coordinateSystemRender.sortObjects = false;
        this.domElement.append(this.coordinateSystemRender.domElement);

        this.coordinateSystemScene = new THREE.Scene();
        this.coordinateSystemScene.add(new THREE.AmbientLight(0x7F7F7F));
        this.coordinateSystemScene.add(this.coordinateSystemVis.getGeometry());

        this.coordinateSystemCamera = new THREE.PerspectiveCamera(60, 200 / 200, 0.5, 200); // width / height

        this.domElement.toggle(false);
    }

    public updateCamera() {
        const theta = this.cameraControl.getAzimuthalAngle();
        const phi = this.cameraControl.getPolarAngle();
        this.coordinateSystemVis.update(new THREE.Vector3(0, 0, 0), theta, phi);

        const worldDir = this.camera.getWorldDirection().clone().multiplyScalar(-6); // what direction the camera is looking; inverse and put on sphere of radius 6
        this.coordinateSystemCamera.position.copy(worldDir);
        this.coordinateSystemCamera.lookAt(new THREE.Vector3(0, 0, 0));

        this.coordinateSystemRender.render(this.coordinateSystemScene, this.coordinateSystemCamera);
    }

    public setSize(widht: number, height: number) {
        this.coordinateSystemCamera.aspect = 1.0;
        this.coordinateSystemCamera.updateProjectionMatrix();

        this.coordinateSystemRender.setSize(widht, height);
    }
}

class CoordinateSystemArrows {
    private geometryGroup: THREE.Object3D;
    private xAxis: THREE.Mesh;
    private yAxis: THREE.Mesh;
    private zAxis: THREE.Mesh;
    private xArrow: THREE.Mesh;
    private yArrow: THREE.Mesh;
    private zArrow: THREE.Mesh;
    private xLabel: THREE.Mesh;
    private yLabel: THREE.Mesh;
    private zLabel: THREE.Mesh;

    private resetObj(object: THREE.Object3D) {
        object.position.set(0, 0, 0);
        object.rotation.set(0, 0, 0);
        object.scale.set(1, 1, 1);
        object.updateMatrix();
    }

    public update(origin: THREE.Vector3, rotateTextTheta: number, rotateTextPhi: number): void {
        const length = 2;

        this.resetObj(this.xAxis);
        this.resetObj(this.yAxis);
        this.resetObj(this.zAxis);
        this.resetObj(this.xArrow);
        this.resetObj(this.yArrow);
        this.resetObj(this.zArrow);
        this.resetObj(this.xLabel);
        this.resetObj(this.yLabel);
        this.resetObj(this.zLabel);

        this.xAxis.translateOnAxis(origin, 1).translateX(length / 2).rotateZ(-Math.PI / 2);
        this.yAxis.translateOnAxis(origin, 1).translateY(length / 2);
        this.zAxis.translateOnAxis(origin, 1).translateZ(length / 2).rotateX(Math.PI / 2);
        this.xArrow.translateOnAxis(origin, 1).translateX(length).rotateZ(-Math.PI / 2);
        this.yArrow.translateOnAxis(origin, 1).translateY(length);
        this.zArrow.translateOnAxis(origin, 1).translateZ(length).rotateX(Math.PI / 2);
        this.xLabel.translateOnAxis(origin, 1).translateX(length).translateY(0.33).translateZ(0.33).rotateY(rotateTextTheta).rotateX(rotateTextPhi + Math.PI * 3 / 2);
        this.yLabel.translateOnAxis(origin, 1).translateY(length).translateX(0.33).translateZ(0.33).rotateY(rotateTextTheta).rotateX(rotateTextPhi + Math.PI * 3 / 2);
        this.zLabel.translateOnAxis(origin, 1).translateZ(length).translateX(0.33).translateY(0.33).rotateY(rotateTextTheta).rotateX(rotateTextPhi + Math.PI * 3 / 2);
    }

    public getGeometry(): THREE.Object3D {
        if (this.geometryGroup) {
            return this.geometryGroup;
        }

        const length = 2;
        var textGeometrySettings = {
            height: 0.001,
            size: 0.6,
            curveSegments: 4,
            font: "optimer", // helvetiker, optimer, gentilis
            weight: "normal", // normal bold
            style: "normal" // normal italic
        };

        // create X/Y/Z Axis
        this.xAxis = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, length), new THREE.MeshBasicMaterial({ color: 0xFF0000 }));
        this.yAxis = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, length), new THREE.MeshBasicMaterial({ color: 0x00FF00 }));
        this.zAxis = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, length), new THREE.MeshBasicMaterial({ color: 0x0000FF }));

        // Create Cones to use as Arrows
        this.xArrow = new THREE.Mesh(new THREE.CylinderGeometry(0, 0.1, 0.33), new THREE.MeshBasicMaterial({ color: 0xFF0000 }));
        this.yArrow = new THREE.Mesh(new THREE.CylinderGeometry(0, 0.1, 0.33), new THREE.MeshBasicMaterial({ color: 0x00FF00 }));
        this.zArrow = new THREE.Mesh(new THREE.CylinderGeometry(0, 0.1, 0.33), new THREE.MeshBasicMaterial({ color: 0x0000FF }));

        // Create Label for each Axis
        this.xLabel = new THREE.Mesh(new THREE.TextGeometry("X", textGeometrySettings), new THREE.MeshBasicMaterial({ color: 0x000000 }));
        this.yLabel = new THREE.Mesh(new THREE.TextGeometry("Y", textGeometrySettings), new THREE.MeshBasicMaterial({ color: 0x000000 }));
        this.zLabel = new THREE.Mesh(new THREE.TextGeometry("Z", textGeometrySettings), new THREE.MeshBasicMaterial({ color: 0x000000 }));

        this.geometryGroup = new THREE.Object3D();
        this.geometryGroup.add(this.xAxis);
        this.geometryGroup.add(this.yAxis);
        this.geometryGroup.add(this.zAxis);
        this.geometryGroup.add(this.xArrow);
        this.geometryGroup.add(this.yArrow);
        this.geometryGroup.add(this.zArrow);
        this.geometryGroup.add(this.xLabel);
        this.geometryGroup.add(this.yLabel);
        this.geometryGroup.add(this.zLabel);

        return this.geometryGroup;
    }
}


